/**
 * ElectronDatabaseAdapter
 *
 * Database adapter for the Electron renderer process.
 * Wraps IPC calls to the main process which uses SQLiteAdapter.
 *
 * Implements IDatabasePort interface for the hexagonal architecture.
 */

/**
 * Create an Electron database adapter
 * Uses window.electronAPI exposed by preload.js
 */
export function createElectronDatabaseAdapter() {
  // Check if running in Electron
  if (!window.electronAPI) {
    throw new Error('ElectronDatabaseAdapter: window.electronAPI not available. Are you running in Electron?')
  }

  const api = window.electronAPI

  return {
    // ============================================
    // Accounts
    // ============================================
    getAccounts: () => api.getAccounts(),
    getAccount: (id) => api.getAccount(id),
    getDefaultAccount: () => api.getDefaultAccount(),
    createAccount: (data) => api.createAccount(data),
    updateAccount: (id, data) => api.updateAccount(id, data),
    deleteAccount: (id) => api.deleteAccount(id),

    // ============================================
    // Applications
    // ============================================
    getApplications: (accountId) => api.getApplications(accountId),
    getApplication: (id) => api.getApplication(id),
    createApplication: (data) => api.createApplication(data),
    updateApplication: (id, data) => api.updateApplication(id, data),
    deleteApplication: (id) => api.deleteApplication(id),

    // ============================================
    // Jobs
    // ============================================
    getJobs: (applicationId) => api.getJobs(applicationId),
    getJob: (id) => api.getJob(id),
    createJob: (data) => api.createJob(data),
    updateJob: (id, data) => api.updateJob(id, data),

    // ============================================
    // Workflows
    // ============================================
    getWorkflows: (applicationId) => api.getWorkflows(applicationId),
    getWorkflow: (id) => api.getWorkflow(id),
    createWorkflow: (data) => api.createWorkflow(data),
    updateWorkflow: (id, data) => api.updateWorkflow(id, data),
    deleteWorkflow: (id) => api.deleteWorkflow(id),

    // ============================================
    // Settings
    // ============================================
    getSettings: () => api.getSettings(),
    updateSettings: (data) => api.updateSettings(data),
    getAWSCredentials: () => api.getAWSCredentials(),
    updateAWSCredentials: (data) => api.updateAWSCredentials(data),
    getAIProvider: (provider) => api.getAIProvider(provider),
    updateAIProvider: (provider, data) => api.updateAIProvider(provider, data),

    // ============================================
    // File Storage & API Settings
    // ============================================
    getFileStorageSettings: () => api.getFileStorageSettings(),
    updateFileStorageSettings: (data) => api.updateFileStorageSettings(data),
  }
}
